import * as React from "react";
import { Icon, type IconTone, Pill, type PillTone, MonoMeta } from "./primitives";
import { IconChevronRight } from "./icons";
import { SmartLink } from "./link-context";

// ---------------------------------------------------------------------------
// HeroCell
// ---------------------------------------------------------------------------
export interface HeroCellProps {
  kickerIcon?: React.ReactNode;
  kickerLabel: React.ReactNode;
  big: React.ReactNode;
  bigTone?: "default" | "success" | "danger";
  desc?: React.ReactNode;
  children?: React.ReactNode;
  cell?: "visit" | "checkin" | "dosing" | "result" | "next" | "line";
}

export function HeroCell({
  kickerIcon,
  kickerLabel,
  big,
  bigTone = "default",
  desc,
  children,
  cell,
}: HeroCellProps) {
  return (
    <div className="hero-cell" data-cell={cell}>
      <div className="hero-kicker">
        {kickerIcon}
        <span>{kickerLabel}</span>
      </div>
      <div className={["hc-big", bigTone !== "default" && bigTone].filter(Boolean).join(" ")}>
        {big}
      </div>
      {desc && <p className="hc-desc">{desc}</p>}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CallRow
// ---------------------------------------------------------------------------
export type CallRail = "default" | "ok" | "fail" | "scr";

export interface CallRowProps {
  rail?: CallRail;
  title: React.ReactNode;
  pill?: { label: React.ReactNode; tone?: PillTone };
  snippet?: React.ReactNode;
  when: { primary: React.ReactNode; secondary?: React.ReactNode };
  href?: string;
  onClick?: () => void;
  showChevron?: boolean;
}

export function CallRow({
  rail = "default",
  title,
  pill,
  snippet,
  when,
  href,
  onClick,
  showChevron = true,
}: CallRowProps) {
  const content = (
    <>
      <span className={["rail", rail !== "default" && rail].filter(Boolean).join(" ")} />
      <div>
        <div className="cb-title">
          {title}
          {pill && <Pill tone={pill.tone}>{pill.label}</Pill>}
        </div>
        {snippet && <p className="cb-snippet">{snippet}</p>}
      </div>
      <MonoMeta primary={when.primary} secondary={when.secondary} className="cb-when" />
      {showChevron && (
        <div className="chev">
          <IconChevronRight />
        </div>
      )}
    </>
  );
  if (href) {
    return (
      <SmartLink className="call-row" href={href}>
        {content}
      </SmartLink>
    );
  }
  return (
    <div className="call-row" onClick={onClick} role={onClick ? "button" : undefined}>
      {content}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CheckRow — UA variant
// ---------------------------------------------------------------------------
export type CheckRail = "default" | "ok" | "rep" | "fail";

export interface CheckRowProps {
  rail?: CheckRail;
  title: React.ReactNode;
  pill?: { label: React.ReactNode; tone?: "default" | "rep" | "fail" };
  snippet?: React.ReactNode;
  when: { primary: React.ReactNode; secondary?: React.ReactNode };
  href?: string;
}

export function CheckRow({ rail = "default", title, pill, snippet, when, href }: CheckRowProps) {
  const body = (
    <>
      <span className={["rail", rail !== "default" && rail].filter(Boolean).join(" ")} />
      <div className="chk-main">
        <div className="t">
          {title}
          {pill && <Pill tone={pill.tone as PillTone}>{pill.label}</Pill>}
        </div>
        {snippet && <div className="s">{snippet}</div>}
      </div>
      <MonoMeta primary={when.primary} secondary={when.secondary} className="chk-when" />
    </>
  );
  if (href) {
    return (
      <SmartLink className="chk-row" href={href}>
        {body}
      </SmartLink>
    );
  }
  return <div className="chk-row">{body}</div>;
}

// ---------------------------------------------------------------------------
// GlanceRow
// ---------------------------------------------------------------------------
export interface GlanceRowProps {
  icon: React.ReactNode;
  iconTone?: IconTone;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  value?: React.ReactNode;
  valueNote?: React.ReactNode;
  "aria-label"?: string;
}

export function GlanceRow({
  icon,
  iconTone = "mute",
  title,
  subtitle,
  value,
  valueNote,
  "aria-label": ariaLabel,
}: GlanceRowProps) {
  return (
    <div className="gl-row" aria-label={ariaLabel}>
      <Icon tone={iconTone} className="gl-ic">
        {icon}
      </Icon>
      <div className="gl-body">
        <div className="t">{title}</div>
        {subtitle && <div className="s">{subtitle}</div>}
      </div>
      {(value || valueNote) && (
        <div className="gl-end">
          {value && <b>{value}</b>}
          {valueNote}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HoursBlock
// ---------------------------------------------------------------------------
export interface HoursCell {
  label: string;
  value: string;
}
export interface HoursBlockProps {
  cells: HoursCell[];
  className?: string;
}
export function HoursBlock({ cells, className = "" }: HoursBlockProps) {
  return (
    <div className={["hours", className].filter(Boolean).join(" ")}>
      {cells.map((c, i) => (
        <div key={i}>
          <div className="lbl">{c.label}</div>
          <div className="val">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ListFooter
// ---------------------------------------------------------------------------
export interface ListFooterProps {
  summary: React.ReactNode;
  action?: { label: React.ReactNode; href?: string; onClick?: () => void };
  className?: string;
}
export function ListFooter({ summary, action, className = "" }: ListFooterProps) {
  return (
    <div className={["calls-footer", "list-footer", className].filter(Boolean).join(" ")}>
      <span>{summary}</span>
      {action &&
        (action.href ? (
          <SmartLink href={action.href}>{action.label}</SmartLink>
        ) : (
          <button type="button" onClick={action.onClick}>
            {action.label}
          </button>
        ))}
    </div>
  );
}
